import { useCallback, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../api/client';
import type { ContentTypeItem } from '../api/types';
import { useApi, useMutation } from '../lib/use-api';
import { EmptyNotice, ErrorNotice, Loading } from '../components/Feedback';

/**
 * Bật/tắt loại học liệu theo đơn vị — plan §5.1.
 *
 * Thư viện cài một bản dùng chung toàn hệ thống; bảng whitelist quyết định
 * đơn vị nào được dùng cái nào. Không có bản ghi = chưa ai giới hạn = cho dùng.
 */
export function ContentTypesPage(): ReactNode {
  const { t } = useTranslation(['admin', 'common']);
  const types = useApi<{ items: ContentTypeItem[] }>('/api/admin/content-types');

  const toggle = useMutation<{ machineName: string; enabled: boolean }, unknown>(
    useCallback(
      (input: { machineName: string; enabled: boolean }) =>
        apiFetch(`/api/admin/content-types/${encodeURIComponent(input.machineName)}`, {
          method: 'PUT',
          body: { enabled: input.enabled },
        }),
      [],
    ),
  );

  const change = async (item: ContentTypeItem, enabled: boolean): Promise<void> => {
    const result = await toggle.run({ machineName: item.machineName, enabled });
    if (result !== undefined) types.reload();
  };

  return (
    <section>
      <h1>{t('contentType.heading')}</h1>
      <p className="hint">{t('contentType.intro')}</p>

      {types.loading ? <Loading /> : null}
      {types.error ? <ErrorNotice error={types.error} onRetry={types.reload} /> : null}
      {toggle.error ? <ErrorNotice error={toggle.error} /> : null}

      {types.data && !types.loading ? (
        types.data.items.length === 0 ? (
          <EmptyNotice />
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">{t('contentType.column.title')}</th>
                <th scope="col">{t('contentType.column.machineName')}</th>
                <th scope="col">{t('contentType.column.version')}</th>
                <th scope="col">{t('contentType.column.enabled')}</th>
              </tr>
            </thead>
            <tbody>
              {types.data.items.map((item) => (
                <tr key={item.machineName}>
                  <td>{item.title}</td>
                  <td className="mono">{item.machineName}</td>
                  <td className="mono">
                    {item.majorVersion}.{item.minorVersion}
                  </td>
                  <td>
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={item.enabled}
                        disabled={toggle.pending}
                        onChange={(event) => void change(item, event.target.checked)}
                      />
                      <span>
                        {item.enabled ? t('contentType.enabled') : t('contentType.disabled')}
                      </span>
                    </label>
                    {!item.explicit ? (
                      <small className="muted"> {t('contentType.usingDefault')}</small>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      ) : null}

      <p className="hint">{t('contentType.disableWarning')}</p>
    </section>
  );
}
